/**
 * Компонент ввода значения для фильтра
 * Динамически выбирает тип инпута в зависимости от типа поля
 */

import { DatePicker, Input, InputNumber, Select } from "antd";
import dayjs from "dayjs";
import { observer } from "mobx-react-lite";
import * as React from "react";

import { ZFilterValue, ZFilterValueType } from "../../types";
import { ControlWithError } from "../ControlWithError/ControlWithError";
import * as styles from "./ValueInput.module.less";

interface PropsValueInput {
  /** Тип значения поля */
  valueType: ZFilterValueType | null;

  /** Текущее значение */
  value: ZFilterValue;

  /** Callback при изменении значения */
  onChange: (value: ZFilterValue) => void;

  /** Placeholder */
  placeholder?: string;

  /** Disabled состояние */
  disabled?: boolean;

  /** Требуется ли ввод значения (некоторые операторы не требуют) */
  requiresValue?: boolean;

  /** Текст ошибки */
  errorText?: string;
}

type PropsValueRenderer = Pick<
  PropsValueInput,
  "disabled" | "errorText" | "onChange" | "placeholder" | "value"
>;

const TextValueInput: React.FC<PropsValueRenderer> = ({
  value,
  onChange,
  placeholder = "Ввод значения",
  disabled,
  errorText,
}) => (
  <Input
    className={styles.valueInput}
    value={value as string}
    onChange={(e) => onChange(e.target.value || null)}
    placeholder={placeholder}
    disabled={disabled}
    status={errorText ? "error" : undefined}
  />
);

const NumberValueInput: React.FC<PropsValueRenderer> = ({
  value,
  onChange,
  placeholder = "Ввод значения",
  disabled,
  errorText,
}) => (
  <InputNumber
    className={styles.valueInput}
    value={value as number}
    onChange={(val) => onChange(val ?? null)}
    placeholder={placeholder}
    disabled={disabled}
    status={errorText ? "error" : undefined}
    style={{ width: "100%" }}
  />
);

const DateTimeValueInput: React.FC<PropsValueRenderer> = ({
  value,
  onChange,
  placeholder = "Ввод значения",
  disabled,
  errorText,
}) => (
  <DatePicker
    className={styles.valueInput}
    value={typeof value === "string" ? dayjs(value) : null}
    onChange={(date) => onChange(date ? date.toISOString() : null)}
    placeholder={placeholder}
    disabled={disabled}
    status={errorText ? "error" : undefined}
    style={{ width: "100%" }}
    showTime
  />
);

const BooleanValueInput: React.FC<PropsValueRenderer> = ({
  value,
  onChange,
  placeholder = "Выбор значения",
  disabled,
  errorText,
}) => (
  <Select
    className={styles.valueInput}
    value={value as boolean}
    onChange={(val) => onChange(val ?? null)}
    placeholder={placeholder}
    disabled={disabled}
    status={errorText ? "error" : undefined}
    optionFilterProp="label"
    options={[
      { value: true, label: "Да" },
      { value: false, label: "Нет" },
    ]}
    allowClear
  />
);

const valueInputRegistry: Record<
  ZFilterValueType,
  React.FC<PropsValueRenderer>
> = {
  TEXT: TextValueInput,
  NUMBER: NumberValueInput,
  DATETIME: DateTimeValueInput,
  BOOLEAN: BooleanValueInput,
};

/**
 * Динамический инпут для значения фильтра
 */
export const ValueInput: React.FC<PropsValueInput> = observer(
  ({
    valueType,
    value,
    onChange,
    placeholder,
    disabled,
    requiresValue = true,
    errorText,
  }) => {
    if (!requiresValue) {
      return null;
    }

    const Renderer = valueType ? valueInputRegistry[valueType] : TextValueInput;

    return (
      <ControlWithError className={styles.valueInputWrap} errorText={errorText}>
        <Renderer
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          errorText={errorText}
        />
      </ControlWithError>
    );
  },
);
